/**
 * useActionableSuggestions - Hook for filtering and flattening actionable suggestions
 *
 * Filters to only show suggestions that need user action:
 * - Set must be PENDING or PARTIALLY_RESOLVED (not RESOLVED or SUPERSEDED)
 * - Suggestion must be PENDING (not already accepted/rejected/completed)
 * - Suggestion action_type must be CREATE or UPDATE (not NO_CHANGE)
 *
 * Used by:
 * - SyncSuggestionCardList: to show cards for visible entities only
 * - SyncSuggestionBadge: to count suggestions on connection lines
 * - useSyncSuggestionsMap: to build lookup maps
 */

import { useMemo } from 'react';

import type {
  FilevineEntitySyncSuggestionSchema,
  FilevineEntitySyncSuggestionSetSchema,
} from '@/services/api/generated/entitySyncService';

import type { FlatSuggestion } from './types';

// ============================================================================
// Constants
// ============================================================================

/** Set statuses where there are still pending suggestions to act on */
const ACTIONABLE_SET_STATUSES = new Set(['PENDING', 'PARTIALLY_RESOLVED']);

export function isPendingAutoPushReview(
  suggestion: FilevineEntitySyncSuggestionSchema,
): boolean {
  const withReview = suggestion as FilevineEntitySyncSuggestionSchema & {
    is_auto_pushed?: boolean;
    review_status?: string | null;
  };
  return (
    withReview.is_auto_pushed === true &&
    suggestion.status === 'COMPLETED' &&
    withReview.review_status === 'PENDING_REVIEW'
  );
}

export function isCompletedReviewedAutoPush(
  suggestion: FilevineEntitySyncSuggestionSchema,
): boolean {
  const withReview = suggestion as FilevineEntitySyncSuggestionSchema & {
    is_auto_pushed?: boolean;
    review_status?: string | null;
  };
  return (
    withReview.is_auto_pushed === true &&
    suggestion.status === 'COMPLETED' &&
    (withReview.review_status === 'ACCEPTED' ||
      withReview.review_status === 'MODIFIED')
  );
}

// ============================================================================
// Types
// ============================================================================

export interface UseActionableSuggestionsOptions {
  /** Suggestion sets from the API */
  suggestionSets: FilevineEntitySyncSuggestionSetSchema[] | undefined;
  /** Optional: Only include suggestions for these Finch entity IDs */
  visibleFinchEntityIds?: Set<string>;
  /** Optional: Only include suggestions for these Filevine entity IDs */
  visibleFilevineEntityIds?: Set<string>;
  /** Include completed auto-push suggestions that were already reviewed. */
  includeReviewedAutoPushed?: boolean;
}

export interface UseActionableSuggestionsResult {
  /** Flattened list of actionable suggestions with their parent sets */
  flatSuggestions: FlatSuggestion[];
  /** Total count of actionable suggestions */
  totalCount: number;
  /** Count of PENDING suggestions (same as totalCount since we only show pending) */
  pendingCount: number;
  /** Count of TO_FINCH suggestions */
  toFinchCount: number;
  /** Count of TO_FILEVINE suggestions */
  toFilevineCount: number;
}

// ============================================================================
// Utility Functions (exported for reuse)
// ============================================================================

/**
 * Check if a suggestion is actionable:
 * - Status is PENDING (not yet accepted/rejected/completed)
 * - Action type is CREATE or UPDATE (not NO_CHANGE)
 */
export function isActionableSuggestion(
  suggestion: FilevineEntitySyncSuggestionSchema,
  includeReviewedAutoPushed = false,
): boolean {
  if (isPendingAutoPushReview(suggestion)) {
    return true;
  }

  if (
    includeReviewedAutoPushed &&
    isCompletedReviewedAutoPush(suggestion)
  ) {
    return true;
  }

  if (!['PENDING', 'ACCEPTED', 'EXECUTING', 'FAILED'].includes(suggestion.status)) {
    return false;
  }

  return (
    suggestion.action_type !== 'NO_CHANGE'
  );
}

/**
 * Check if a suggestion set has actionable suggestions.
 * The set must be PENDING or PARTIALLY_RESOLVED, and must contain
 * at least one PENDING suggestion with a real change.
 */
export function hasActionableSuggestions(
  set: FilevineEntitySyncSuggestionSetSchema,
  includeReviewedAutoPushed = false,
): boolean {
  const suggestions = set.suggestions ?? [];
  if (
    suggestions.some(
      (suggestion) =>
        isPendingAutoPushReview(suggestion) ||
        (includeReviewedAutoPushed &&
          isCompletedReviewedAutoPush(suggestion)),
    )
  ) {
    return true;
  }
  if (!ACTIONABLE_SET_STATUSES.has(set.status)) return false;
  return suggestions.some((suggestion) =>
    isActionableSuggestion(suggestion, includeReviewedAutoPushed),
  );
}

/**
 * Get actionable suggestions from a set (PENDING with real changes only)
 */
export function getActionableSuggestions(
  set: FilevineEntitySyncSuggestionSetSchema,
  includeReviewedAutoPushed = false,
): FilevineEntitySyncSuggestionSchema[] {
  return (set.suggestions ?? []).filter((suggestion) =>
    isActionableSuggestion(suggestion, includeReviewedAutoPushed),
  );
}

/**
 * Check if a suggestion set matches any visible entity.
 * Returns true if:
 * - No visibility filters provided (show all)
 * - Set's finch_entity_id is in visibleFinchEntityIds
 * - Set's filevine_collection_item_id is in visibleFilevineEntityIds
 */
export function matchesVisibleEntities(
  set: FilevineEntitySyncSuggestionSetSchema,
  visibleFinchEntityIds?: Set<string>,
  visibleFilevineEntityIds?: Set<string>,
): boolean {
  // If no filters provided, match all
  if (!visibleFinchEntityIds && !visibleFilevineEntityIds) {
    return true;
  }

  // Check if set matches a visible Finch entity
  if (visibleFinchEntityIds && set.finch_entity_id) {
    if (visibleFinchEntityIds.has(set.finch_entity_id)) {
      return true;
    }
  }

  // Check if set matches a visible Filevine entity
  if (visibleFilevineEntityIds && set.filevine_collection_item_id) {
    if (visibleFilevineEntityIds.has(set.filevine_collection_item_id)) {
      return true;
    }
  }

  return false;
}

/**
 * Flatten suggestion sets into individual actionable suggestions
 * with optional entity visibility filtering.
 *
 * Only includes:
 * - Sets that are PENDING or PARTIALLY_RESOLVED
 * - Suggestions that are PENDING with CREATE or UPDATE action
 */
export function flattenActionableSuggestions(
  suggestionSets: FilevineEntitySyncSuggestionSetSchema[] | undefined,
  visibleFinchEntityIds?: Set<string>,
  visibleFilevineEntityIds?: Set<string>,
  includeReviewedAutoPushed = false,
): FlatSuggestion[] {
  if (!suggestionSets) return [];

  return suggestionSets
    .filter((set) =>
      hasActionableSuggestions(set, includeReviewedAutoPushed),
    )
    .filter((set) =>
      matchesVisibleEntities(
        set,
        visibleFinchEntityIds,
        visibleFilevineEntityIds,
      ),
    )
    .flatMap((set) =>
      getActionableSuggestions(set, includeReviewedAutoPushed).map((suggestion) => ({
        suggestion,
        set,
      })),
    );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook that filters and flattens actionable suggestions.
 *
 * Only returns suggestions where there is a change to be made:
 * - Set is PENDING or PARTIALLY_RESOLVED
 * - Suggestion is PENDING with a CREATE or UPDATE action
 *
 * @example
 * ```tsx
 * const { flatSuggestions, pendingCount } = useActionableSuggestions({
 *   suggestionSets: data?.suggestion_sets,
 *   visibleFinchEntityIds: finchIds,
 *   visibleFilevineEntityIds: filevineIds,
 * });
 * ```
 */
export function useActionableSuggestions({
  suggestionSets,
  visibleFinchEntityIds,
  visibleFilevineEntityIds,
  includeReviewedAutoPushed = false,
}: UseActionableSuggestionsOptions): UseActionableSuggestionsResult {
  return useMemo(() => {
    const flatSuggestions = flattenActionableSuggestions(
      suggestionSets,
      visibleFinchEntityIds,
      visibleFilevineEntityIds,
      includeReviewedAutoPushed,
    );

    let toFinchCount = 0;
    let toFilevineCount = 0;

    flatSuggestions.forEach(({ suggestion }) => {
      if (suggestion.direction === 'TO_FINCH') {
        toFinchCount += 1;
      } else if (suggestion.direction === 'TO_FILEVINE') {
        toFilevineCount += 1;
      }
    });

    return {
      flatSuggestions,
      totalCount: flatSuggestions.length,
      pendingCount: flatSuggestions.length,
      toFinchCount,
      toFilevineCount,
    };
  }, [
    suggestionSets,
    visibleFinchEntityIds,
    visibleFilevineEntityIds,
    includeReviewedAutoPushed,
  ]);
}

export default useActionableSuggestions;
